/**
 * Wann hat dieses Geraet einen Konflikt verloren? (KONZEPT.md §15.3, §15.4)
 *
 * Die Regel selbst -- die neuere Buchung gewinnt -- steht in
 * `@thw/core-domain` (`ueberstimmtVon`) und gilt auf Server und Geraet gleich.
 * Hier geht es um die Frage davor: **Wann ist das ein Konflikt, und wann der
 * gewoehnliche Lauf der Dinge?**
 *
 * Ein Konflikt ist es, wenn die eigene Buchung beim **Eintreffen** schon
 * ueberstimmt war. Dann hat sie nie gegolten, und wer sie gebucht hat, glaubt
 * etwas Falsches. Das passiert, wenn das Geraet offline war, waehrend ein
 * anderes spaeter gebucht und frueher uebertragen hat.
 *
 * Kein Konflikt ist es, wenn nach der eigenen Buchung jemand anders den
 * Artikel weiterbewegt. Das ist Alltag -- darueber einen Hinweis zu bekommen,
 * hiesse, bei jeder Umlagerung durch einen Kameraden gewarnt zu werden.
 *
 * Den Unterschied macht die Folgenummer des Servers: Sie sagt, in welcher
 * Reihenfolge die Buchungen **angekommen** sind.
 */

import { ueberstimmtVon, type ArtikelKern } from "@thw/core-domain";
import type { Buchung } from "@thw/contracts";

/**
 * Liefert die Buchung, die beim Eintreffen der eigenen schon gewonnen hatte,
 * oder `null`.
 *
 * `log` ist das Log des Artikels, mindestens bis zur Folgenummer der eigenen
 * Buchung. Eine Buchung, die noch in der Warteschlange liegt, traegt die
 * hoechste Folgenummer -- sie kommt ja erst noch an.
 *
 * Mit `geraet` zaehlt eine Buchung desselben Geraets nicht als Gegner: Wer
 * zweimal hintereinander bucht, hat seine Meinung geaendert und braucht keinen
 * Hinweis darauf. Ohne (`null`) zaehlt jede -- so fragt die Historie.
 */
export function konfliktFinden(
  artikel: ArtikelKern,
  eigene: Buchung,
  log: readonly Buchung[],
  geraet: string | null,
): Buchung | null {
  const vorher = log.filter((b) => b.id === eigene.id || b.folge < eigene.folge);
  const gewinner = ueberstimmtVon(artikel, eigene, vorher);
  if (gewinner === null || gewinner.geraet === geraet) return null;
  return gewinner;
}
